// ============================================================
// ByOurConsent.com -- Author Profile Page
// /author/:name
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import api    from '../utils/api';

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

const AuthorPage = () => {
    const { name }  = useParams();
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/articles/author/${encodeURIComponent(name)}`)
            .then(r => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [name]);

    if (loading) return (
        <div style={{ minHeight: '100vh', background: '#f5f0e8' }}><Nav />
            <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>
        </div>
    );

    if (!data) return null;

    const { author, articles, chapters } = data;
    const displayName = author.display_name || name;
    const photoUrl = author.photo_url
        ? (author.photo_url.startsWith('http') ? author.photo_url : `http://localhost:5002${author.photo_url}`)
        : null;

    return (
        <>
            <Helmet>
                <title>{displayName} — By Our Consent</title>
                <meta name="description" content={author.bio || `Articles and contributions by ${displayName}`} />
            </Helmet>

            <Nav />

            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #1e2a3a 0%, #2e3d52 100%)', borderBottom: '2px solid #8b1a1a', padding: '2rem 1.5rem' }}>
                <div style={{ maxWidth: '760px', margin: '0 auto' }}>
                    <Link to="/articles" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Articles
                    </Link>
                    <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', marginTop: '1rem' }}>
                        {/* Photo */}
                        {photoUrl ? (
                            <img src={photoUrl} alt={displayName}
                                style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #8b1a1a' }} />
                        ) : (
                            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#8b1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontFamily: "'EB Garamond', Georgia, serif", fontWeight: 600, color: '#f5f0e8', flexShrink: 0 }}>
                                {displayName[0]}
                            </div>
                        )}
                        <div>
                            <div style={{ fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginBottom: '4px' }}>
                                Contributor
                            </div>
                            <h1 style={{ fontSize: '30px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.15, marginBottom: '4px' }}>
                                {displayName}
                            </h1>
                            {author.organization && (
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8b9a7a' }}>
                                    {author.organization}
                                </div>
                            )}
                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7a8a', marginTop: '4px' }}>
                                {articles.length} {articles.length === 1 ? 'article' : 'articles'}
                                {chapters.length > 0 && ` · ${chapters.length} chapter${chapters.length > 1 ? 's' : ''} led`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: articles.length > 0 ? '1fr 240px' : '1fr', gap: '2rem', alignItems: 'start' }}>

                    {/* Main content */}
                    <div>
                        {/* Bio */}
                        {author.bio && (
                            <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '0.5px solid #c8beaa' }}>
                                <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '10px' }}>
                                    About
                                </div>
                                <div style={{ fontSize: '15px', fontFamily: 'sans-serif', color: '#2a3040', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                                    {author.bio}
                                </div>
                                {author.website_url && (
                                    <a href={author.website_url} target="_blank" rel="noopener noreferrer"
                                        style={{ display: 'inline-block', marginTop: '10px', background: '#8b1a1a', color: '#f5f0e8', padding: '6px 16px', fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '2px' }}>
                                        Visit website →
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Articles */}
                        {articles.length > 0 && (
                            <div style={{ marginBottom: '2rem' }}>
                                <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '12px' }}>
                                    Articles ({articles.length})
                                </div>
                                {articles.map((a, i) => (
                                    <Link key={a.id} to={`/articles/${a.slug}`} style={{ textDecoration: 'none' }}>
                                        <div style={{ padding: '12px 0', borderBottom: '0.5px solid #c8beaa' }}
                                            onMouseEnter={e => e.currentTarget.querySelector('.title').style.color = '#8b1a1a'}
                                            onMouseLeave={e => e.currentTarget.querySelector('.title').style.color = '#1e2a3a'}>
                                            <div className="title" style={{ fontSize: '16px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '4px', transition: 'color 0.15s' }}>
                                                {a.title}
                                            </div>
                                            {a.excerpt && (
                                                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#5a6070', lineHeight: 1.5, marginBottom: '4px' }}>
                                                    {a.excerpt.length > 120 ? a.excerpt.slice(0, 120) + '...' : a.excerpt}
                                                </div>
                                            )}
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                                {fmtDate(a.published_at)} · {a.view_count || 0} views
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {articles.length === 0 && (
                            <div style={{ padding: '2rem', textAlign: 'center', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                No published articles yet.
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    {articles.length > 0 && (
                        <div>
                            {/* Chapters led */}
                            {chapters.length > 0 && (
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1rem' }}>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '10px' }}>
                                        Chapters led
                                    </div>
                                    {chapters.map(c => (
                                        <Link key={c.id} to={`/chapters/${c.slug}`} style={{ textDecoration: 'none', display: 'block', marginBottom: '8px' }}>
                                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>{c.name}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                                {c.member_count} members{c.location && ` · ${c.location}`}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {/* Contact */}
                            {author.website_url && (
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem' }}>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '10px' }}>
                                        Links
                                    </div>
                                    <a href={author.website_url} target="_blank" rel="noopener noreferrer"
                                        style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', textDecoration: 'none' }}>
                                        Website →
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </>
    );
};

export default AuthorPage;
